/** @format */

import { Alert, Backdrop, Box, CircularProgress } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { DataGrid } from '@mui/x-data-grid';
import { EVENT_USER_LIST_HEADER } from "../../helpers/Constants";
import SendIcon from '@mui/icons-material/Send';
import { Button, Tooltip, Typography } from "@mui/material";
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { GridToolbarExport } from '@mui/x-data-grid';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { useHistory } from "react-router-dom";
import { EmailFrom } from "../../helpers/Constants";
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import RemoveIcon from '@mui/icons-material/Remove';
import HomeIcon from '@mui/icons-material/Home';
import Avatar from '@mui/material/Avatar';

export default function EventUserList(props) {
    const isMobile = useMediaQuery("(max-width:768px)");
    const queryClient = new QueryClient();
    const id = props.location.state.id;
    const [loading, setLoading] = useState(true);
    const [eventsData, setEventsData] = useState(null);
    const noData = typeof eventsData === "undefined" || eventsData === null || eventsData?.id == 0;
    const history = useHistory();
    /** Modal for sending email */
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [error, setError] = useState(null);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };
    /** End Modal for sending email */

    const timeout = () => {
        setTimeout(() => {
            setLoading(false);
        }, 1000);
    }
    const [filter, setFilter] = useState("all");

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    useEffect(() => {
        setLoading(true);
        handleGetData();
    }, [filter]);

    const handleGetData = () => {
        try {
            fetch(`/api/Adjuster/GetListOfUsersByEventId?eventId=${id}&userScope=${filter}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            })
                .then((data) => data.json())
                .then((data) => {
                    setEventsData(data?.users.$values);
                    timeout();
                })
                .catch((error) => {
                    console.error("Error fetching data: ", error);
                    timeout();
                });
        } catch (ex) {
            timeout();
        }
    }

    const handleExport = () => {
        setLoading(true);
        try {
            const timezoneOffsetMinutes = new Date().getTimezoneOffset();
            fetch(`/api/Adjuster/GetListOfUsersByEventIdAndExportToExcel?eventId=${id}`, {
                method: 'GET',
                headers: {
                    'X-TimeZone-Offset-Minutes': String(timezoneOffsetMinutes)
                }
            })
                .then((response) => response.json())
                .then((blob) => {
                    if (blob.fileContents !== undefined && blob.fileContents.length > 0) {
                        const link = document.createElement('a');
                        link.href = `data: ${blob.contentType};base64, ${blob.fileContents}`;
                        link.setAttribute(
                            'download',
                            `${blob.fileName}`,
                        );
                        // Append to html link element page
                        document.body.appendChild(link);
                        // Start download
                        link.click();
                        // Clean up and remove the link
                        link.parentNode.removeChild(link);
                    }
                    timeout();
                })
                .catch((error) => {
                    console.error("Error fetching data: ", error);
                    timeout();
                });
        } catch (ex) {
            timeout();
        }
    };

    const handleCreateUser = () => {
        setLoading(true);
        setTimeout(() => {
            localStorage.setItem('tempDefaultEventId', id);
            window.location.replace("/Search");
            setLoading(false);
        }, 3000);
    }

    const handleSendEmail = () => {
        setError(null);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError("Please enter a valid email address.");
            return;
        }
        setLoading(true);
        fetch(`/api/Adjuster/GetListOfUsersByEventIdAndSendEmail?eventId=${id}&toEmail=${email}&fromEmail=${EmailFrom}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        })
            .then((response) => response.json())
            .then((data) => {
                console.log("Email sent successfully: ", data);
                timeout();
            })
            .catch((error) => {
                console.error("Error sending email: ", error);
                timeout();
            });
        handleClose();
    };

    const handleMainMenu = () => {
        history.push({ pathname: '/EventManager' });
    }

    // State to track selected rows
    const [selectedRows, setSelectedRows] = useState([]);

    // Handler to remove selected users from UI
    const handleRemoveUsers = async () => {
        if (!eventsData) return;

        // Create an array of objects with userId, eventId, and isInERP for each selected row
        const selectedUserObjects = selectedRows.map(row => ({
            userId: row.id,
            eventId: id,
            isInERP: row.existingUser
        }));
        //console.log(selectedUserObjects);
        if (window.confirm("Are you sure you want to remove selected user(s) from the event?")) {
            // Call API to mark users as soft deleted in the database
            try {
                setLoading(true);
                await fetch('/api/Event/SoftDeleteMultiEntry', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(
                        selectedUserObjects
                    )
                });
                // Optimistically update UI
                const newData = eventsData.filter(row => !selectedRows.includes(row.id));
                setEventsData(newData);
                setSelectedRows([]);
                setLoading(true);
                handleGetData();
            } catch (error) {
                console.error("Error soft deleting users: ", error);
            } finally {
                setLoading(false);
            }
        }
    };

    // Assume you have a prop or context to determine if the user is admin
    const roles = localStorage.getItem("roles") ? localStorage.getItem("roles")?.split(",") : [];
    const isAdmin = roles &&
        roles?.length > 0 &&
        roles?.find(
            (role) => role?.toLowerCase().includes("checkinappadmin"))
        ;
    return (
        <>
            <Helmet>
                <title> Event User List | Check In App </title>
            </Helmet>

            <Box sx={{ width: isMobile ? "100%" : "95%" }}>
                {noData ? (
                    <Alert severity="info" sx={{ fontSize: "15px", marginTop: "8px" }}>
                        No record was found.
                    </Alert>
                ) : (
                    <QueryClientProvider client={queryClient}>
                        <div className="bg-white rounded-lg p-8 space-y-4 flex flex-col shadow-2xl">
                            <div className="text-center p-4 space-y-6 ">
                                <h2 className="text-xl text-red-600 font-bold">Below you can view the list of users that have checked in.</h2>
                                <Box sx={{ padding: "12px" }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'right', marginBottom: '16px' }}>
                                        <FormControl component="fieldset">
                                            <RadioGroup row name="filter" defaultValue="ALL" onChange={handleFilterChange}>
                                                <FormControlLabel value="ERP" control={<Radio />} label="in ERP" />
                                                <FormControlLabel value="NOERP" control={<Radio />} label="not in ERP" />
                                                <FormControlLabel value="ALL" control={<Radio />} label="All" />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                    <DataGrid
                                        checkboxSelection={isAdmin}
                                        onRowSelectionModelChange={(ids) => {
                                            if (!isAdmin) return;
                                            const visibleRows = (eventsData || []);
                                            const selected = visibleRows.filter(row => ids.includes(row.id));
                                            setSelectedRows(selected);
                                        }}
                                        rowSelectionModel={selectedRows.map(row => row.id)}
                                        components={{
                                            Toolbar: GridToolbarExport,
                                        }}
                                        rows={eventsData || []}
                                        columns={EVENT_USER_LIST_HEADER}
                                        sx={{
                                            '& .MuiDataGrid-columnHeaders': {
                                                color: 'rgb(220 38 38)', // text-red-600
                                                borderBottom: '1px solid black', // Add bottom border to header
                                            },
                                            '& .MuiDataGrid-columnSeparator': {
                                                display: 'none',
                                            },
                                            '& .MuiDataGrid-cell': {
                                                borderBottom: 'none', // Remove bottom border from cells
                                            },
                                            '& .MuiDataGrid-viewport': {
                                                border: 'none', // Remove border around the viewport
                                            },
                                            '& .MuiDataGrid-root': {
                                                border: 'none', // Remove border around the DataGrid
                                                borderColor: 'transparent', // Remove border color
                                            },
                                            '& .MuiDataGrid-row': {
                                                border: 'none', // Remove border around the rows
                                            },
                                        }}
                                        checkboxSelectionVisibleOnly={false}
                                    />
                                    {isAdmin && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 0 }}>
                                            <Tooltip title="Delete User(s)">
                                                <Button
                                                    variant="contained"
                                                    disabled={selectedRows.length === 0}
                                                    onClick={handleRemoveUsers}
                                                    sx={{
                                                        bgcolor: '#7f1d1d',
                                                        '&:hover': { bgcolor: '#431407' },
                                                        color: 'white',
                                                        borderRadius: '5px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 0,
                                                    }}
                                                    className="bg-[#7f1d1d] hover:bg-red-900 text-white rounded-full px-6 py-3 flex items-center justify-center space-x-2"
                                                >
                                                    Delete user(s)
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Box>
                            </div>
                            <Box
                                sx={{
                                    marginTop: "16px",
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    paddingX: "12px",
                                }}
                            >
                                {/* Modal for sending email */}
                                <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                                    <DialogTitle>Send Email</DialogTitle>
                                    <DialogContent>
                                        <TextField
                                            autoFocus
                                            margin="dense"
                                            id="email"
                                            label="Email Address"
                                            type="email"
                                            fullWidth
                                            variant="standard"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                        <div className="flex justify-center">
                                            {error && <div className="text-red-500">{error}</div>}
                                        </div>
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleClose}>Cancel</Button>
                                        <Button onClick={handleSendEmail}>Send</Button>
                                    </DialogActions>
                                </Dialog>
                                {/* End Modal for sending email */}
                                <Box sx={{ width: "16px" }} /> {/* Add space between buttons */}
                                <Button
                                    sx={{ color: "black", width: "32px", minWidth: "0px" }}
                                    onClick={() => handleExport()}
                                >
                                    <Tooltip openDelay={50} placement="top" title="Download">
                                        <SimCardDownloadIcon fontSize="large" />
                                    </Tooltip>
                                </Button>
                                <Box sx={{ width: "16px" }} /> {/* Add space between buttons */}
                                <Button
                                    sx={{ color: "black", width: "32px", minWidth: "0px" }}
                                    onClick={() => handleCreateUser()}
                                >
                                    <Tooltip openDelay={50} placement="top" title="Add A User">
                                        <PersonAddAltIcon fontSize="large" />
                                    </Tooltip>
                                </Button>
                            </Box>
                            <div className="flex justify-center py-2 space-x-4">
                                <div>
                                    <button className="bg-red-900 text-white rounded-full px-7 py-2" onClick={handleMainMenu}>
                                        Main Menu
                                    </button>
                                </div>
                            </div>
                        </div>
                    </QueryClientProvider>
                )}
                <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            </Box>
        </>
    );
}
